import React, { useCallback, useMemo } from "react";
import { Popconfirm, Table, Tooltip, Typography } from "antd";
import { CommentOutlined, DeleteOutlined } from "@ant-design/icons";
import moment from "moment-timezone";
import { ColumnType } from "antd/lib/table";
import uniq from "lodash/uniq";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";

import {
  TaxServiceDepartment,
  TaxServiceDetails,
  TaxServiceName,
  TaxServiceStatus,
} from "../../../api/graphql/schema.generated";
import { formatDate } from "../TaxDeclaration/utils";
import { formatAmountCurrency } from "../../../utils/amount";
import TaxServicesTableExpandRow from "./TaxServicesTableExpandRow";
import { useDeleteTaxServiceMutation } from "../../../api/graphql/mutations/taxService/deleteTaxService.generated";

const getFullName = (data: TaxServiceDetails) => {
  return data.kontaxUser.kontistUser.legalName || "-";
};

const getEmail = (data: TaxServiceDetails) =>
  data.kontaxUser.kontistUser.email!;

// Only open services can be deleted. Billed or paid ones stay as they are.
const isServiceEditable = (record: TaxServiceDetails) => {
  return (
    record.status === TaxServiceStatus.BLOCKED ||
    record.status === TaxServiceStatus.WILL_BE_BILLED
  );
};

// A service goes into the EasyBill export when it is not excluded and has a
// charge. Free services have no charge, so they never go into the export.
const isIncludedInExport = (record: TaxServiceDetails) =>
  !record.excludedFromExport && record.chargeAmountInEuro != null;

const getTexts = (t: TFunction<"ServiceTracker", undefined>) => ({
  columnLabel: (columnName: string) =>
    t(`serviceTracker.tableHeaders.${columnName}`),
  departmentName: (name: string) => t(`serviceTracker.departmentsName.${name}`),
  serviceName: (name: string) => t(`serviceTracker.servicesName.${name}`),
  yesNo: (value: boolean) =>
    t(`serviceTracker.inExportValues.${value ? "yes" : "no"}`),
  delete: t("serviceTracker.delete"),
  deleteRecurringFeeRow: (period: string) =>
    t("serviceTracker.deleteRecurringFeeRow", { period }),
});

/*
 * A generated recurring-fee row is not generated again once deleted
 * (SMT-1366), so its month then goes unbilled unless it is recorded as a flat
 * rate. The confirmation says so instead of the generic question.
 */
const getDeleteConfirmTitle = (
  record: TaxServiceDetails,
  texts: ReturnType<typeof getTexts>
) =>
  record.department === TaxServiceDepartment.RECURRING_FEES &&
  record.periodStart
    ? texts.deleteRecurringFeeRow(moment(record.periodStart).format("MM/YYYY"))
    : "Sure to delete?";

const TaxServicesTable = ({
  taxServices,
  onRefresh,
}: {
  taxServices: TaxServiceDetails[] | undefined;
  onRefresh: () => void;
}) => {
  const [deleteTaxServiceMutation] = useDeleteTaxServiceMutation();

  const { t } = useTranslation("ServiceTracker");
  const texts = getTexts(t);

  const handleDeleteTaxService = useCallback(
    async (taxServiceId: string) => {
      await deleteTaxServiceMutation({
        variables: { taxServiceId },
      });
      onRefresh();
    },
    [deleteTaxServiceMutation, onRefresh]
  );

  const yearsFilter = useMemo(
    () =>
      uniq(taxServices?.map(({ year }) => (year ? `${year}` : "-"))).map(
        (year) => ({
          text: year,
          value: year,
        })
      ),
    [taxServices]
  );

  const serviceNamesFilter = useMemo(
    () =>
      uniq(taxServices?.map(({ name }) => name)).map((name) => ({
        text: texts.serviceName(name),
        value: name,
      })),
    [taxServices, texts]
  );

  const serviceDepartmentsFilter = useMemo(
    () =>
      uniq(taxServices?.map(({ department }) => department)).map(
        (department) => ({
          text: texts.departmentName(department),
          value: department,
        })
      ),
    [taxServices, texts]
  );

  const createdByFilter = useMemo(
    () =>
      uniq(taxServices?.map(({ createdBy }) => createdBy)).map((createdBy) => ({
        text: createdBy.split("@")[0],
        value: createdBy,
      })),
    [taxServices]
  );

  const columns: ColumnType<TaxServiceDetails>[] = useMemo(
    () => [
      {
        title: texts.columnLabel("date"),
        key: "createdAt",
        dataIndex: "createdAt",
        defaultSortOrder: "descend",
        render: (createdAt: Date) => {
          return formatDate(createdAt);
        },
        sorter: (a, b) => (moment(a.createdAt).isAfter(b.createdAt) ? 1 : -1),
      },
      {
        title: <CommentOutlined />,
        key: "internalNote",
        dataIndex: "internalNote",
        render: (internalNote: string) => {
          return internalNote.length ? <CommentOutlined /> : "";
        },
      },
      {
        title: texts.columnLabel("clientName"),
        render: (row: TaxServiceDetails) => getFullName(row),
        sorter: (a, b) => {
          const nameA = getFullName(a);
          const nameB = getFullName(b);
          return nameA.localeCompare(nameB);
        },
      },
      {
        title: texts.columnLabel("clientEmail"),
        render: (row: TaxServiceDetails) => getEmail(row),
        sorter: (a, b) => getEmail(a).localeCompare(getEmail(b)),
      },
      {
        title: texts.columnLabel("field"),
        key: "department",
        dataIndex: "department",
        render: (department: TaxServiceDepartment) =>
          texts.departmentName(department),
        sorter: (a, b) => a.department.localeCompare(b.department),
        filters: serviceDepartmentsFilter,
        filterSearch: true,
        onFilter: (value: boolean | React.Key, record: TaxServiceDetails) =>
          record.department === value,
      },
      {
        title: texts.columnLabel("service"),
        key: "name",
        dataIndex: "name",
        render: (name: TaxServiceName) => texts.serviceName(name),
        sorter: (a, b) => a.name.localeCompare(b.name),
        filters: serviceNamesFilter,
        filterSearch: true,
        onFilter: (value: boolean | React.Key, record: TaxServiceDetails) =>
          record.name === value,
      },
      {
        title: texts.columnLabel("hours"),
        key: "hours",
        dataIndex: "hours",
        render: (hours) => hours ?? "-",
        sorter: (a, b) => (a.hours ?? 0) - (b.hours ?? 0),
      },
      {
        title: texts.columnLabel("quantity"),
        key: "quantity",
        dataIndex: "quantity",
        render: (quantity) => quantity ?? "-",
        sorter: (a, b) => (a.quantity ?? 0) - (b.quantity ?? 0),
      },
      {
        title: texts.columnLabel("amount"),
        key: "chargeAmountInEuro",
        dataIndex: "chargeAmountInEuro",
        // Net amount billed for the service (computed server-side). Free services
        // have no charge → shown as "-".
        render: (chargeAmountInEuro: number | null | undefined) =>
          chargeAmountInEuro == null
            ? "-"
            : formatAmountCurrency(chargeAmountInEuro),
        sorter: (a, b) =>
          (a.chargeAmountInEuro ?? 0) - (b.chargeAmountInEuro ?? 0),
      },
      {
        title: texts.columnLabel("taxYear"),
        key: "year",
        dataIndex: "year",
        render: (year) => year ?? "-",
        sorter: (a, b) => (a.year ?? 0) - (b.year ?? 0),
        filters: yearsFilter,
        filterSearch: true,
        onFilter: (value: boolean | React.Key, record: TaxServiceDetails) =>
          (record.year ?? "-").toString() === value.toString(),
      },
      {
        title: texts.columnLabel("operator"),
        key: "createdBy",
        dataIndex: "createdBy",
        render: (createdBy: string) => createdBy.split("@")[0],
        sorter: (a, b) => a.createdBy.localeCompare(b.createdBy),
        filters: createdByFilter,
        filterSearch: true,
        onFilter: (value: boolean | React.Key, record: TaxServiceDetails) =>
          record.createdBy === value,
      },
      {
        title: texts.columnLabel("inExport"),
        key: "inExport",
        render: (_: unknown, record: TaxServiceDetails) =>
          texts.yesNo(isIncludedInExport(record)),
        sorter: (a, b) =>
          Number(isIncludedInExport(a)) - Number(isIncludedInExport(b)),
      },
      {
        key: "delete",
        render: (_: unknown, record: TaxServiceDetails) => {
          // An icon, not a word: "Delete" wrapped letter by letter in the
          // narrow column. The label stays for the tooltip and screen readers.
          if (!isServiceEditable(record)) {
            return (
              <Typography.Link disabled aria-label={texts.delete}>
                <DeleteOutlined />
              </Typography.Link>
            );
          }
          // Tooltip below the icon: the confirmation opens above it, and a
          // tooltip on top would cover its OK button.
          return (
            <Tooltip title={texts.delete} placement="bottom">
              <Popconfirm
                title={getDeleteConfirmTitle(record, texts)}
                onConfirm={() => handleDeleteTaxService(record.id)}
              >
                <Typography.Link aria-label={texts.delete}>
                  <DeleteOutlined />
                </Typography.Link>
              </Popconfirm>
            </Tooltip>
          );
        },
      },
    ],
    [
      texts,
      serviceNamesFilter,
      yearsFilter,
      createdByFilter,
      serviceDepartmentsFilter,
      handleDeleteTaxService,
    ]
  );

  return (
    <Table
      columns={columns}
      dataSource={taxServices}
      pagination={{
        total: taxServices?.length,
        defaultPageSize: 10,
        pageSizeOptions: ["10", "20", "50"],
        showSizeChanger: true,
      }}
      rowKey="id"
      style={{ width: "100%" }}
      rowClassName="expandable"
      expandable={{
        expandedRowRender: (taxService: TaxServiceDetails) => {
          return (
            <TaxServicesTableExpandRow
              taxService={taxService}
              onRefresh={onRefresh}
            />
          );
        },
        defaultExpandAllRows: false,
      }}
    />
  );
};

export default TaxServicesTable;
